import React, { useState, useEffect } from "react";

import AvatarEditor from "react-avatar-editor";
import MaskedInput from "react-text-mask";
import { toast, ToastContainer } from "react-toastify";

import { withTranslation } from "react-i18next";

import { Link, withRouter, useParams, useHistory } from "react-router-dom";

import myConfig from "../../configs/config";
import axios from "axios";
import "./CreateOperator.css";
import BackButton from "../../components/BackButton";
import OperatorPlanQuotaBar from "./OperatorPlanQuotaBar";

function formatApiError(error, fallback) {
  const data = error.response && error.response.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return data;
  }
  if (data.detail) {
    return Array.isArray(data.detail) ? data.detail[0] : String(data.detail);
  }
  return (
    Object.keys(data)
      .map((key) => {
        const value = data[key];
        if (Array.isArray(value)) {
          return value[0];
        }
        return typeof value === "string" ? value : "";
      })
      .filter(Boolean)
      .join(" ") || fallback
  );
}

function OperatorCreate(props) {
  const params = useParams();
  const history = useHistory();
  const width = 120;

  const farmId = params.farm_id;
  const operatorId = params.operator_id === undefined ? -1 : params.operator_id;

  let hasOperator = operatorId !== -1;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [identity, setIdentity] = useState("");
  const [hours_week, setHoursWeek] = useState("");
  const [salary, setSalary] = useState("");
  const [picture, setPicture] = useState(null);

  // Worker Profile fields for attendance system
  const [enableAttendance, setEnableAttendance] = useState(false);
  const [cpf, setCpf] = useState("");
  const [department, setDepartment] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [workerProfileId, setWorkerProfileId] = useState(null);
  const [planIncluded, setPlanIncluded] = useState(true);
  const [planStatus, setPlanStatus] = useState(null);

  async function getCurrentOperator() {
    let OPERATOR_URL = `${myConfig.API_URL}/operator/${operatorId}/?farm_id=${farmId}`;
    let response = await axios({
      baseURL: OPERATOR_URL,
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("client-token")}`,
      },
    });

    let data = response.data;

    setName(data.nome);
    setPhone(data.telefone);
    setIdentity(data.ident);
    setHoursWeek(data.horas_semanais);
    setSalary(data.salario);
    setPicture(data.picture);
    setPlanIncluded(Boolean(data.plan_included));

    // Load worker profile if exists
    if (data.id) {
      await getWorkerProfile(data.id);
    }
  }

  async function loadPlanStatus() {
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/farm/${farmId}/plan-operators/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      const status = response.data.plan_operator_status || null;
      setPlanStatus(status);
      if (!hasOperator && status && status.available_slots <= 0) {
        setPlanIncluded(false);
      }
    } catch (error) {
      setPlanStatus(null);
    }
  }

  async function getWorkerProfile(operatorId) {
    try {
      let WORKER_PROFILE_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/by-operator/${operatorId}/`;
      let response = await axios({
        baseURL: WORKER_PROFILE_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      let data = response.data;
      setWorkerProfileId(data.id);
      setEnableAttendance(Boolean(data.is_active));

      setCpf(data.cpf || "");
      setDepartment(data.department || "");
      setJobTitle(data.job_title || "");
      setEmployeeId(data.employee_id || "");
    } catch (error) {
      console.log("Worker profile not found or error loading it");
    }
  }

  async function updateOperator() {
    try {
      let UPDATE_OPERATOR = `${myConfig.API_URL}/operator/${operatorId}/?farm_id=${farmId}`;

      let response = await axios({
        baseURL: UPDATE_OPERATOR,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
        data: getFormData(),
      });

      let status = response.status;

      if (status === 200) {
        const profileSaved = await saveWorkerProfile(operatorId);
        if (!profileSaved) {
          return;
        }

        toast.success(
          `${props.t("Operador")}  ${props.t("atualizado com sucesso")}!`,
          {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
        setTimeout(() => {
          history.push(`/${farmId}/operator/`);
        }, 2000);
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error,
          props.t("Não foi possível atualizar o operador.")
        ),
        {
          position: "bottom-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
    }
  }

  async function createOperator() {
    try {
      let CREATE_OPERATOR = `${myConfig.API_URL}/operator/?farm_id=${farmId}`;

      let response = await axios({
        baseURL: CREATE_OPERATOR,
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
        data: getFormData(),
      });

      let status = response.status;

      if (status === 201) {
        const createdId = response.data && response.data.id;
        if (createdId) {
          const profileSaved = await saveWorkerProfile(createdId);
          if (!profileSaved) {
            try {
              await axios.delete(
                `${myConfig.API_URL}/operator/${createdId}/?farm_id=${farmId}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "client-token"
                    )}`,
                  },
                }
              );
            } catch (rollbackError) {
              console.error(
                "Failed to rollback operator after CPF error",
                rollbackError
              );
            }
            return;
          }
        }

        toast.success(
          `${props.t("Operador")}  ${props.t("cadastrado com sucesso")}!`,
          {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
        setTimeout(() => {
          history.push(`/${farmId}/operator/`);
        }, 2000);
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error,
          props.t("Não foi possível cadastrar o operador.")
        ),
        {
          position: "bottom-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
    }
  }

  async function saveWorkerProfile(savedOperatorId) {
    try {
      const workerData = {
        cpf: cpf.replace(/\D/g, ""),
        department: department || undefined,
        job_title: jobTitle || undefined,
        employee_id: employeeId || undefined,
        is_active: enableAttendance,
        operator_id: Number(savedOperatorId),
      };

      if (workerProfileId) {
        // Update existing worker profile
        let UPDATE_WORKER_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/${workerProfileId}/`;
        await axios({
          baseURL: UPDATE_WORKER_URL,
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
            "Content-Type": "application/json",
          },
          data: workerData,
        });
      } else {
        // Create new worker profile
        let CREATE_WORKER_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/`;
        await axios({
          baseURL: CREATE_WORKER_URL,
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
            "Content-Type": "application/json",
          },
          data: workerData,
        });
      }
      return true;
    } catch (error) {
      console.error("Error saving worker profile:", error);
      const errorDetail = formatApiError(
        error,
        props.t("Não foi possível salvar o CPF do operador.")
      );
      toast.error(errorDetail, {
        position: "bottom-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return false;
    }
  }

  function getFormData() {
    let form_data = new FormData();
    if (typeof picture === "object" && picture !== null) {
      form_data.append("picture", picture, picture.name);
    }
    form_data.append("farm", params.farm_id);
    form_data.append("nome", name);
    form_data.append("telefone", phone);
    form_data.append("ident", identity || name);
    form_data.append("horas_semanais", hours_week || "40.0");
    if (salary) {
      form_data.append("salario", salary);
    }
    form_data.append("plan_included", planIncluded ? "true" : "false");
    return form_data;
  }

  function handleSave(e) {
    e.preventDefault();

    if (!name.trim()) {
      toast.error(props.t("Por favor, preencha o nome do operador"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length !== 11) {
      toast.error(props.t("CPF deve conter 11 dígitos"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    if (hasOperator) {
      // PUT
      updateOperator();
    } else {
      //POST
      createOperator();
    }
  }
  useEffect(() => {
    loadPlanStatus();
    if (hasOperator) getCurrentOperator();
  }, []);

  return (
    <div id="operator_create_container">
      <div style={{ marginBottom: "15px" }}>
        <BackButton to={`/${farmId}/operator`} label={props.t("Voltar")} />
      </div>
      <h2>{hasOperator ? "Editar Operator" : "Cadastrar Operador"}</h2>

      <OperatorPlanQuotaBar farmId={farmId} />

      <form method="POST" onSubmit={handleSave} encType="multipart/form-data">
        <div className="avatar-user">
          <AvatarEditor
            className="avatar"
            image={picture}
            width={120}
            height={120}
            style={{ border: " 1px solid #f2f2f2f2" }}
            border={50}
            borderRadius={width / (100 / 50)} //BORDER FOCUS 50%
            color={[255, 255, 255, 0.6]} // RGBA
            scale={1.2}
            rotate={0}
          />
        </div>

        <div className="form-group">
          <label htmlFor="picture">{props.t("Foto")}</label>
          <input
            type="file"
            name="picture"
            onChange={(e) => setPicture(e.target.files[0])}
            title="Insira uma foto"
            id="id_picture"
          />
        </div>

        <div className="form-group">
          <label htmlFor="name">{props.t("Nome")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            name="name"
            maxLength={35}
            className="form-control"
            placeholder={props.t("Nome")}
            required
            id="name"
          />
        </div>

        {planStatus && planStatus.limit > 0 ? (
          <div className="form-group">
            <div className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={planIncluded}
                  disabled={
                    !planIncluded &&
                    planStatus.available_slots <= 0 &&
                    !hasOperator
                  }
                  onChange={(e) => setPlanIncluded(e.target.checked)}
                />{" "}
                {props.t("Incluir nas vagas gratuitas do plano")}
              </label>
            </div>
            <small className="help-block text-muted">
              {planIncluded
                ? props.t(
                    "Este operador usará uma vaga gratuita do plano ({{used}} de {{limit}} em uso).",
                    {
                      used: planStatus.included_count,
                      limit: planStatus.limit,
                    }
                  )
                : props.t(
                    "Este operador será cadastrado como adicional (fora das {{limit}} vagas gratuitas).",
                    { limit: planStatus.limit }
                  )}
            </small>
          </div>
        ) : null}

        <div className="form-group">
          <label htmlFor="phone">{props.t("Telefone")}</label>

          <MaskedInput
            mask={[
              "(",
              /[1-9]/,
              /\d/,
              ")",
              " ",
              /\d/,
              /\d/,
              /\d/,
              /\d/,
              /\d/,
              "-",
              /\d/,
              /\d/,
              /\d/,
              /\d/,
            ]}
            name="cell_phone"
            onChange={(e) => setPhone(e.target.value)}
            value={phone}
            maxLength={16}
            className="form-control"
            placeholder={props.t("Telefone")}
          />
        </div>

        <div className="form-group">
          <label htmlFor="cpf">
            {props.t("CPF")} <span style={{ color: "red" }}>*</span>
          </label>
          <MaskedInput
            mask={[
              /\d/,
              /\d/,
              /\d/,
              ".",
              /\d/,
              /\d/,
              /\d/,
              ".",
              /\d/,
              /\d/,
              /\d/,
              "-",
              /\d/,
              /\d/,
            ]}
            name="cpf"
            onChange={(e) => setCpf(e.target.value)}
            value={cpf}
            className="form-control"
            placeholder="000.000.000-00"
            required
            id="cpf"
          />
          <small style={{ display: "block", marginTop: "5px", color: "#666" }}>
            {props.t(
              "O CPF e o nome do operador serão usados para login no aplicativo móvel"
            )}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="department">{props.t("Departamento")}</label>
          <input
            type="text"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            name="department"
            maxLength={100}
            className="form-control"
            placeholder={props.t("Agricultura, Manutenção, etc.")}
            id="department"
          />
        </div>

        <div className="form-group">
          <label htmlFor="job_title">{props.t("Cargo")}</label>
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            name="job_title"
            maxLength={100}
            className="form-control"
            placeholder={props.t("Operador de Trator, Mecânico, etc.")}
            id="job_title"
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={enableAttendance}
              onChange={(e) => setEnableAttendance(e.target.checked)}
              style={{ marginRight: "10px" }}
            />
            {props.t("Habilitar registro de ponto")}
          </label>
          <small style={{ display: "block", marginTop: "5px", color: "#666" }}>
            {props.t(
              "Marque esta opção para permitir que o operador registre ponto via aplicativo móvel"
            )}
          </small>
          <small style={{ display: "block", marginTop: "5px", color: "#666" }}>
            {props.t(
              "O operador poderá registrar ponto no aplicativo com o CPF e o nome cadastrados."
            )}
          </small>
        </div>

        <div align="center">
          <input
            type="submit"
            className="btn btn-primary"
            value={props.t("Salvar")}
          />
          <Link to={`/${farmId}/operator/`} className="btn btn-default">
            {props.t("Cancelar")}
          </Link>
        </div>
      </form>
      <ToastContainer />
    </div>
  );
}

export default withTranslation()(withRouter(OperatorCreate));
